import {
  Component,
  computed,
  contentChild,
  contentChildren,
  input,
  output
} from '@angular/core';

import { NgTemplateOutlet, SlicePipe } from '@angular/common';
import { slideInOutAnimation } from '../../utils/animation/animation';

import { ExpandIconComponent } from '../expand-icon/expand-icon.component';
import { IconButtonComponent } from '../icon-button/icon-button.component';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { TableCardHeaderComponent } from './header/table-card-header.component';
import { TableCardColumnTemplateDirective } from './column/table-card-column-template.directive';
import type {
  InputSignal,
  OutputEmitterRef,
  Signal
} from '@angular/core';

export type TableCardPaddingSize = 'zero' | 'small' | 'regular';

@Component({
  selector: 'td-wb-table-card',
  templateUrl: 'table-card.component.html',
  styleUrls: ['table-card.component.scss'],
  animations: [slideInOutAnimation],
  imports: [NgTemplateOutlet, ExpandIconComponent, IconButtonComponent, SlicePipe,
    TranslatePipe],
  host: {
    '[class.td-wb-table-card--expandable]': 'isExpandable()',
    '[class.td-wb-table-card--embedded]': 'isEmbedded()',
    '[class.td-wb-table-card--small-padding]': 'isSmallPadding()'
  }
})
export class TableCardComponent{

  //Optional Input: If parent binds [expanded], this component is expandable
  readonly expanded: InputSignal<boolean | undefined> = input<boolean | undefined>(undefined);

  //Emits next value so parents can update their state
  readonly expandedChange: OutputEmitterRef<boolean> = output<boolean>();

  readonly noOfSummaryColumns: InputSignal<number> = input<number>(1);
  readonly noOfSummaryRows: InputSignal<number> = input<number>(1);
  readonly paddingSize: InputSignal<TableCardPaddingSize> = input<TableCardPaddingSize>('regular');

  readonly header: Signal<TableCardHeaderComponent | undefined> = contentChild(TableCardHeaderComponent);
  readonly headerId: Signal<string> = computed(() => this.header()?.id ?? '');

  readonly columnTemplates: Signal<ReadonlyArray<TableCardColumnTemplateDirective>> = contentChildren(TableCardColumnTemplateDirective);

  //Derived styling
  readonly isEmbedded: Signal<boolean> = computed(() => this.paddingSize() === 'zero');
  readonly isSmallPadding: Signal<boolean> = computed(() => this.paddingSize() === 'small');

  //Expandable when consumer binds [expanded]
  readonly isExpandable: Signal<boolean> = computed(() => this.expanded() !== undefined);

  //Template safe boolean: If not bound, treat as collapsed. If bound, reflect parents state.
  readonly isExpanded: Signal<boolean> = computed(() => this.expanded() ?? false);

  toggleExpanded(event: Event): void {
    event.stopPropagation();
    if (!this.isExpandable()) { return; }
    const next = !this.isExpanded();
    this.expandedChange.emit(next);
  }

  readonly summaryRows: Signal<Array<Array<TableCardColumnTemplateDirective | undefined>>> = computed(() =>  {
    const columns = this.columnTemplates();
    const numColumns = this.noOfSummaryColumns();
    const numRows = this.noOfSummaryRows();

    const result: Array<Array<TableCardColumnTemplateDirective | undefined>> = [];

    if (!numColumns || !numRows || columns.length === 0) {
      return result;
    }

    const perRow = Math.ceil(numColumns / numRows);
    let current: Array<TableCardColumnTemplateDirective | undefined> = [];

    columns.forEach((item: TableCardColumnTemplateDirective, index: number)=> {
      if (index < numColumns){
        if (index % perRow === 0){
          current = [];
          result.push(current);
        }
        current.push(item);
      }
    });

    if (result.length > 0){
      const last = result[result.length - 1];
      while (last.length < perRow) last.push(undefined);
    }
    return result;
  });

  getLabelledBy(): string {
    return `${this.headerId()}-details ${this.headerId()}`;
  }
}
